/**
 * Per-tenant brand fonts — the dynamic counterpart to the static registry in
 * fonts.ts. Users upload .ttf/.otf/.woff/.woff2 files in Settings; the backend
 * parses the real family/weight/style and exposes them at GET /api/fonts/mine
 * (own uploads + team). This module is the single source of truth for:
 *
 *   - fetching the tenant font list (module-level cached; invalidate on upload)
 *   - registering each face with the browser via the FontFace Web API, so the
 *     Remotion Player and SVG previews paint the real glyphs
 *   - feeding the text-rich font picker (useTenantFonts / getTenantFamilies)
 *   - resolving embeddable face sources for svgFontEmbed (via the resolver we
 *     register into fonts.ts)
 *
 * All network fetches carry the Bearer auth header — the /fonts/mine `url`
 * points at /assets/<id>/file which 401s without it. We deliberately do NOT
 * touch loadAllFonts() in fonts.ts: that path is shared with the render worker
 * (no auth token, no browser) and must stay tenant-agnostic. Tenant faces are
 * loaded separately, app-side, after auth.
 */

import { useSyncExternalStore } from "react";
import { apiUrl } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import {
  registerTenantFaceResolver,
  type FontFaceInfo,
} from "./fonts";
import type { TenantFont } from "@/types/asset";

// ── External store (so React pickers re-render when the list changes) ────────

let _fonts: TenantFont[] = [];
const listeners = new Set<() => void>();

function setFonts(next: TenantFont[]): void {
  _fonts = next;
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): TenantFont[] {
  return _fonts;
}

/** React hook: the current tenant font list. Empty until fetchTenantFonts
 *  resolves (or when the user has no brand fonts). */
export function useTenantFonts(): TenantFont[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Distinct tenant family names (a family may ship multiple weight faces). */
export function getTenantFamilies(): string[] {
  return Array.from(new Set(_fonts.map((f) => f.family)));
}

/** All faces uploaded for a tenant family. */
export function getTenantFaces(family: string): TenantFont[] {
  return _fonts.filter((f) => f.family === family);
}

// ── Auth-carrying fetch ──────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const { token, activeTeamId } = useAuthStore.getState();
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (activeTeamId) h["X-Team-Id"] = activeTeamId;
  return h;
}

// ── Fetch + cache ─────────────────────────────────────────────────────────────

let cache: Promise<TenantFont[]> | null = null;

/** Fetch the tenant font list. Module-level cached — repeated callers share
 *  one in-flight/settled promise until invalidateTenantFonts() is called. */
export function fetchTenantFonts(): Promise<TenantFont[]> {
  if (cache) return cache;
  cache = (async () => {
    const { token } = useAuthStore.getState();
    if (!token) {
      setFonts([]);
      return [];
    }
    try {
      const res = await fetch(apiUrl("/fonts/mine"), { headers: authHeaders() });
      if (!res.ok) return _fonts;
      const list = (await res.json()) as TenantFont[];
      setFonts(Array.isArray(list) ? list : []);
      return _fonts;
    } catch {
      // Network error — keep whatever we had; caller degrades to registry fonts.
      return _fonts;
    }
  })();
  return cache;
}

/** Drop the cached list so the next fetchTenantFonts() hits the network. */
export function invalidateTenantFonts(): void {
  cache = null;
}

// ── FontFace registration ─────────────────────────────────────────────────────

/** family:weight:style keys already registered with document.fonts. */
const addedFaceKeys = new Set<string>();

async function loadOneFace(f: TenantFont): Promise<void> {
  const key = `${f.family}:${f.weight}:${f.style}`;
  if (addedFaceKeys.has(key)) return;
  try {
    const res = await fetch(apiUrl(f.url), { headers: authHeaders() });
    if (!res.ok) return;
    const buf = await res.arrayBuffer();
    const face = new FontFace(f.family, buf, {
      weight: String(f.weight),
      style: f.style || "normal",
      display: "block",
    });
    await face.load();
    document.fonts.add(face);
    addedFaceKeys.add(key);
  } catch {
    // Degrade silently — the family's CSS fallback stack still renders.
  }
}

/**
 * Ensure every tenant face is registered with the browser. Fetches the list
 * (cached) then loads any face not already added. Safe to call repeatedly —
 * already-registered faces are skipped. No-op outside a browser (render worker,
 * SSR).
 */
export async function loadTenantFontFaces(): Promise<void> {
  if (typeof document === "undefined" || !("fonts" in document)) return;
  const list = await fetchTenantFonts();
  await Promise.all(list.map((f) => loadOneFace(f)));
}

/**
 * Invalidate, refetch, and (re)register faces — call after an upload or delete
 * so the picker options and preview glyphs reflect the change immediately.
 */
export async function reloadTenantFonts(): Promise<TenantFont[]> {
  invalidateTenantFonts();
  const list = await fetchTenantFonts();
  await loadTenantFontFaces();
  return list;
}

// ── Resolver registration (for fonts.ts getFontFaceInfo / capabilities) ───────

function resolveTenantFaceInfo(family: string): FontFaceInfo | undefined {
  const faces = getTenantFaces(family);
  if (faces.length === 0) return undefined;
  return {
    family,
    faces: faces.map((f) => ({
      weight: Number(f.weight),
      url: f.url,
      authed: true,
      style: f.style || "normal",
      format: normalizeFormat(f.format),
    })),
  };
}

/** Map a server format/extension string to a CSS `format()` keyword. */
export function normalizeFormat(format: string): string {
  const f = (format || "").toLowerCase();
  if (f.includes("woff2")) return "woff2";
  if (f.includes("woff")) return "woff";
  if (f.includes("otf") || f.includes("opentype")) return "opentype";
  if (f.includes("ttf") || f.includes("truetype")) return "truetype";
  return "woff2";
}

registerTenantFaceResolver(resolveTenantFaceInfo);
